"use client";

import { Button } from "@cmp-src/primitives/button";
import { IconButton } from "@cmp-src/primitives/icon-button/IconButton";
import { Textarea } from "@cmp-src/primitives/textarea";
import type {
  WorkplaceRequestCommentAttachment,
  WorkplaceRequestCommentAttachmentInput
} from "@gettactic/api";
import { Paperclip } from "lucide-react";
import { useRef } from "react";
import { usePendingAttachments } from "@/features/workplace-requests/hooks/usePendingAttachments";
import { generateTicketUpload } from "@/features/workplace-requests/lib/ticketUpload";
import { CommentAttachmentsTray } from "./CommentAttachmentsTray";

interface CommentEditorProps {
  value: string;
  onChange: (value: string) => void;
  /** The attachments the comment currently has; the editor starts from them. */
  initialAttachments: WorkplaceRequestCommentAttachment[];
  onCancel: () => void;
  /**
   * `attachments` is passed only when the list actually changed — omitting it
   * tells the backend to keep the ones the comment already has.
   */
  onSave: (attachments?: WorkplaceRequestCommentAttachmentInput[]) => void;
  isSaving: boolean;
}

/**
 * Inline editor for an existing comment: its text plus the files attached to it.
 * Mounted only while a comment is being edited, so it always starts from that
 * comment's current attachments. Editing is authenticated-only (guests never see
 * the control), hence the fixed upload endpoint.
 */
export const CommentEditor: React.FC<CommentEditorProps> = ({
  value,
  onChange,
  initialAttachments,
  onCancel,
  onSave,
  isSaving
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachments = usePendingAttachments({
    generateUpload: generateTicketUpload,
    initial: initialAttachments
  });

  const hasReadyAttachment = attachments.attachments.some(
    (attachment) => attachment.status === "ready"
  );
  const canSave =
    (value.trim().length > 0 || hasReadyAttachment) &&
    !isSaving &&
    !attachments.isUploading;

  const handleSave = () => {
    if (!canSave) return;
    onSave(attachments.isDirty ? attachments.toInput() : undefined);
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length > 0) {
      attachments.addFiles(files);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-brand border border-neutral-200 bg-white px-3 pb-2 shadow-xs focus-within:border-neutral-300">
        <CommentAttachmentsTray
          attachments={attachments.attachments}
          onRemove={attachments.remove}
          onRetry={attachments.retry}
        />

        <Textarea
          value={value}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            onChange(event.target.value)
          }
          onPaste={(event: React.ClipboardEvent<HTMLTextAreaElement>) => {
            const files = Array.from(event.clipboardData.files);
            if (files.length === 0) return;
            event.preventDefault();
            attachments.addFiles(files);
          }}
          className="max-h-56 min-h-20 w-full resize-none border-0 bg-transparent px-0 py-2 shadow-none focus-visible:ring-0"
        />
      </div>

      <div className="flex items-center justify-between gap-2">
        <IconButton
          icon={Paperclip}
          size="sm"
          variant="ghost"
          onClick={() => fileInputRef.current?.click()}
          disabled={isSaving || attachments.remainingSlots === 0}
          aria-label="Attach files"
          title={
            attachments.remainingSlots === 0
              ? "Attachment limit reached"
              : "Attach files"
          }
          className="text-neutral-400 hover:text-neutral-700"
        />
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={handleFilesSelected}
        />

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!canSave}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};
